/**
 * @file manifestCache — the ONE place manifests are fetched + parsed.
 *
 * Both the current week and every archived week flow through here, so there is
 * a single loading pipeline and a single manifest parser (manifestNormalizer).
 * Results are cached for the lifetime of the session and identical concurrent
 * requests are de-duplicated, so browsing the archive never re-downloads the
 * same manifest.
 *
 * (Unity downloads the heavy game ASSETS itself and keeps its own cache — this
 * module only handles the small JSON manifests the web layer reads.)
 */

import { getJson } from './httpClient';
import { normalizeCompetition } from './manifestNormalizer';

/**
 * @typedef {import('../models/manifest').Competition} Competition
 */

/** url -> resolved Competition (completed fetches). @type {Map<string, Competition>} */
const cache = new Map();

/** url -> in-flight promise (dedupes concurrent requests). @type {Map<string, Promise<Competition>>} */
const inFlight = new Map();

/**
 * Fetch + normalize a manifest, using the session cache. Repeated calls for the
 * same URL resolve from memory; concurrent calls share one network request.
 *
 * @param {string} url
 * @returns {Promise<Competition>}
 */
export function getManifest(url) {
  if (!url) return Promise.reject(new Error('Manifest URL is required.'));

  const cached = cache.get(url);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(url);
  if (pending) return pending;

  const promise = getJson(url)
    .then((raw) => {
      const competition = normalizeCompetition(raw, { sourceUrl: url });
      cache.set(url, competition);
      inFlight.delete(url);
      return competition;
    })
    .catch((err) => {
      inFlight.delete(url); // never cache failures — allow a later retry
      throw err;
    });

  inFlight.set(url, promise);
  return promise;
}

/** Whether a manifest is already resolved in the session cache. */
export function isManifestCached(url) {
  return cache.has(url);
}

/** Clear the cache (mainly for tests). */
export function clearManifestCache() {
  cache.clear();
  inFlight.clear();
}
